import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import './PaintDatabase.css';

const API_BASE = (process.env.REACT_APP_MATCH_API || 'https://k6aslecb84.execute-api.us-west-2.amazonaws.com/prod').replace(/\/$/, '');

const BRAND_ORDER = ['Citadel', 'Vallejo', 'Army Painter', 'AK Interactive', 'PRO Acryl'];
const FINISHES = ['All', 'matte', 'gloss', 'satin'];
const PAGE_SIZE = 60;

function swatchHex(hex) {
  return hex && hex.length === 9 ? hex.slice(0, 7) : hex;
}

function finishLabel(f) {
  return f ? f.charAt(0).toUpperCase() + f.slice(1) : '';
}

export default function PaintDatabase() {
  const [paints, setPaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [search, setSearch] = useState('');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedFinish, setSelectedFinish] = useState('All');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetch(`${API_BASE}/paints`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => setPaints(data.paints || []))
      .catch(err => setFetchError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const brands = useMemo(() => {
    const inData = new Set(paints.map(p => p.brand));
    return BRAND_ORDER.filter(b => inData.has(b));
  }, [paints]);

  const brandCounts = useMemo(() => {
    const counts = {};
    for (const p of paints) counts[p.brand] = (counts[p.brand] || 0) + 1;
    return counts;
  }, [paints]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return paints.filter(p => {
      if (q) {
        const inName = p.name.toLowerCase().includes(q);
        const inBrand = p.brand.toLowerCase().includes(q);
        const inRange = p.range?.toLowerCase().includes(q);
        if (!inName && !inBrand && !inRange) return false;
      }
      if (selectedBrands.length > 0 && !selectedBrands.includes(p.brand)) return false;
      if (selectedFinish !== 'All' && p.finish !== selectedFinish) return false;
      return true;
    });
  }, [paints, search, selectedBrands, selectedFinish]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const visiblePage = Math.min(page, pageCount || 1);
  const paginated = filtered.slice((visiblePage - 1) * PAGE_SIZE, visiblePage * PAGE_SIZE);
  const isFiltered = search || selectedBrands.length > 0 || selectedFinish !== 'All';

  function handleSearch(val) { setSearch(val); setPage(1); }
  function toggleBrand(brand) {
    setSelectedBrands(prev =>
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
    setPage(1);
  }
  function handleFinish(finish) { setSelectedFinish(finish); setPage(1); }
  function clearAll() { setSearch(''); setSelectedBrands([]); setSelectedFinish('All'); setPage(1); }

  return (
    <div className="paint-database-page">
      <Helmet>
        <title>Paint Database – Hobby Paint Finder | Browse All Hobby Paints</title>
        <meta
          name="description"
          content="Browse our complete database of hobby miniature paints from Citadel, Vallejo, Army Painter, AK Interactive, and PRO Acryl. Filter by brand, finish, and search by name."
        />
      </Helmet>

      <Header />

      <main className="pdb-main">
        <section className="pdb-hero">
          <div className="container">
            <h1 className="pdb-title">Paint Database</h1>
            <p className="pdb-subtitle">
              {loading
                ? 'Loading paint database…'
                : <>Browse all <strong>{paints.length.toLocaleString()}</strong> paints across {brands.length} major brands. Full transparency into every color we match against.</>
              }
            </p>

            {!loading && !fetchError && (
              <div className="pdb-brand-stats">
                {brands.map(b => (
                  <button
                    key={b}
                    className={`pdb-brand-stat ${selectedBrands.includes(b) ? 'active' : ''}`}
                    onClick={() => toggleBrand(b)}
                    title={`Filter by ${b}`}
                  >
                    <strong>{brandCounts[b] ?? 0}</strong>
                    <span>{b}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {loading && (
          <div className="pdb-loading">
            <div className="pdb-spinner" aria-label="Loading paints" />
            <p>Loading paint database…</p>
          </div>
        )}

        {fetchError && (
          <div className="pdb-error">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <path d="M12 9v4" /><path d="m12 17 .01 0" />
            </svg>
            <p>Failed to load paints: {fetchError}</p>
            <button className="pdb-retry" onClick={() => { setFetchError(null); setLoading(true); }}>
              Try again
            </button>
          </div>
        )}

        {!loading && !fetchError && (
          <>
            <div className="pdb-controls">
              <div className="container">
                <div className="pdb-search-wrap">
                  <svg className="pdb-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="search"
                    className="pdb-search"
                    placeholder="Search by name, brand, or range…"
                    value={search}
                    onChange={e => handleSearch(e.target.value)}
                    aria-label="Search paints"
                  />
                  {search && (
                    <button className="pdb-search-clear" onClick={() => handleSearch('')} aria-label="Clear search">
                      ×
                    </button>
                  )}
                </div>

                <div className="pdb-filter-row">
                  <div className="pdb-filter-group">
                    <span className="pdb-filter-label">Finish</span>
                    <div className="pdb-pills">
                      {FINISHES.map(f => (
                        <button
                          key={f}
                          className={`pdb-pill ${selectedFinish === f ? 'active' : ''}`}
                          onClick={() => handleFinish(f)}
                        >
                          {f === 'All' ? 'All finishes' : finishLabel(f)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pdb-results-bar">
                  <span className="pdb-count">
                    {filtered.length.toLocaleString()} paint{filtered.length !== 1 ? 's' : ''}
                    {isFiltered && ` of ${paints.length.toLocaleString()}`}
                  </span>
                  {isFiltered && (
                    <button className="pdb-clear" onClick={clearAll}>Clear all filters</button>
                  )}
                </div>
              </div>
            </div>

            <div className="pdb-body">
              <div className="container">
                {paginated.length === 0 ? (
                  <div className="pdb-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <p>No paints match your filters.</p>
                    <button className="pdb-clear" onClick={clearAll}>Clear filters</button>
                  </div>
                ) : (
                  <div className="pdb-grid">
                    {paginated.map(paint => (
                      <div key={`${paint.brand}-${paint.slug}`} className="pdb-card">
                        <div
                          className="pdb-swatch"
                          style={{ backgroundColor: swatchHex(paint.hex) }}
                          aria-label={`Color: ${swatchHex(paint.hex)}`}
                        />
                        <div className="pdb-card-body">
                          <p className="pdb-paint-name">{paint.name}</p>
                          <p className="pdb-paint-meta">
                            <span className="pdb-paint-brand">{paint.brand}</span>
                            {paint.range && <span className="pdb-paint-range">{paint.range}</span>}
                          </p>
                          <div className="pdb-card-footer">
                            <code className="pdb-hex">{swatchHex(paint.hex)}</code>
                            {paint.finish && (
                              <span className={`pdb-finish-badge pdb-finish--${paint.finish}`}>
                                {finishLabel(paint.finish)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {pageCount > 1 && (
                  <div className="pdb-pagination" role="navigation" aria-label="Pagination">
                    <button
                      className="pdb-page-btn"
                      disabled={visiblePage === 1}
                      onClick={() => setPage(p => p - 1)}
                      aria-label="Previous page"
                    >
                      ← Prev
                    </button>
                    <div className="pdb-page-numbers">
                      {Array.from({ length: pageCount }, (_, i) => i + 1)
                        .filter(n => n === 1 || n === pageCount || Math.abs(n - visiblePage) <= 2)
                        .reduce((acc, n, idx, arr) => {
                          if (idx > 0 && n - arr[idx - 1] > 1) acc.push('…');
                          acc.push(n);
                          return acc;
                        }, [])
                        .map((n, i) =>
                          n === '…'
                            ? <span key={`ellipsis-${i}`} className="pdb-page-ellipsis">…</span>
                            : (
                              <button
                                key={n}
                                className={`pdb-page-btn ${n === visiblePage ? 'active' : ''}`}
                                onClick={() => setPage(n)}
                                aria-label={`Page ${n}`}
                                aria-current={n === visiblePage ? 'page' : undefined}
                              >
                                {n}
                              </button>
                            )
                        )
                      }
                    </div>
                    <button
                      className="pdb-page-btn"
                      disabled={visiblePage === pageCount}
                      onClick={() => setPage(p => p + 1)}
                      aria-label="Next page"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
